// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { ParseKeys, TFunction } from 'i18next';

/**
 * Maps instrument types from API to i18n translation keys. If an instrument type is
 * not found in the mapping, the original value is returned.
 */
const INSTRUMENT_TO_TRANSLATION_KEY: Record<
  string,
  | [ParseKeys<'components'>, { ns: 'components' }]
  | [ParseKeys<'glossary'>, { ns: 'glossary' }]
> = {
  unlisted_equity: [
    'onboarding-questions.investment-experience.q_exp_unlisted_equity.label',
    { ns: 'components' },
  ],
  listed_equity: [
    'onboarding-questions.investment-experience.q_exp_listed_equity.label',
    { ns: 'components' },
  ],
  mutual_funds: [
    'onboarding-questions.investment-experience.q_exp_mutual_funds.label',
    { ns: 'components' },
  ],
  etf_simple: [
    'onboarding-questions.investment-experience.q_exp_etf_simple.label',
    { ns: 'components' },
  ],
  gov_bonds: [
    'onboarding-questions.investment-experience.q_exp_gov_bonds.label',
    { ns: 'components' },
  ],
  complex_bonds: [
    'onboarding-questions.investment-experience.q_exp_complex_bonds.label',
    { ns: 'components' },
  ],
  hybrid_products: [
    'onboarding-questions.investment-experience.q_exp_hybrid_products.label',
    { ns: 'components' },
  ],
  structured_products: [
    'onboarding-questions.investment-experience.q_exp_structured_unguaranteed.label',
    { ns: 'components' },
  ],
  cash: ['glossary.cash', { ns: 'glossary' }],
  deposits: [
    'onboarding-questions.investment-experience.q_exp_structured_guaranteed.label',
    { ns: 'components' },
  ],
  derivatives_exchange: [
    'onboarding-questions.investment-experience.q_exp_exchange_derivatives.label',
    { ns: 'components' },
  ],
  derivatives_otc: [
    'onboarding-questions.investment-experience.q_exp_otc_derivatives.label',
    { ns: 'components' },
  ],
  alternative_investments: [
    'onboarding-questions.investment-experience.q_exp_alternative_invest.label',
    { ns: 'components' },
  ],
};

/**
 * Maps an instrument type to a human-readable name using i18n translations. If the
 * instrument type is not found in the mapping, returns the original value
 * unchanged.
 *
 * @param instrument - The instrument type from the API (e.g., 'unlisted_equity',
 *   'mutual_funds')
 * @param t - The i18next translation function
 * @returns The translated instrument name, or the original value if not found in
 *   mapping
 */
export const mapInstrumentToProductFamily = (
  instrument: string,
  t: TFunction,
): string => {
  const translationConfig = INSTRUMENT_TO_TRANSLATION_KEY[instrument];

  if (!translationConfig) {
    return instrument;
  }

  const [key, options] = translationConfig;
  return t(key, options) as string;
};

/**
 * Maps an array of instrument types to human-readable product family names.
 *
 * @param instruments - Array of instrument types from the API
 * @param t - The i18next translation function
 * @returns Array of translated instrument names
 */
export const mapInstrumentsToProductFamilies = (
  instruments: string[] | null | undefined,
  t: TFunction,
): string[] => {
  if (!instruments || instruments.length === 0) {
    return [];
  }

  return instruments.map(instrument => mapInstrumentToProductFamily(instrument, t));
};
